import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";
import * as XLSX from "xlsx";

interface RequestStatusItem {
  id: number;
  productId: number;
  productName: string;
  requestFromPOS: number;
  requestFromPosCode: string;
  requestToPOS: number;
  requestToPosCode: string;
  quantity: number;
  sentQuantity: number;
  batchNo: string;
  statusId: number;
  status: string;
  requestedDate: string;
  respondedDate: string | null;
}

interface RequestStatusDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedPosIds: number[];
}

const RequestStatusDialog: React.FC<RequestStatusDialogProps> = ({
  visible,
  onHide,
  selectedPosIds,
}) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState<RequestStatusItem[]>([]);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedRequestedBy, setSelectedRequestedBy] = useState<string | null>(null);
  const [selectedRequestedTo, setSelectedRequestedTo] = useState<string | null>(null);
  const [selectedBatchNo, setSelectedBatchNo] = useState<string | null>(null);
  const [keywordSearch, setKeywordSearch] = useState<string>("");

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchRequestStatus = async () => {
    if (!selectedPosIds || selectedPosIds.length === 0) return;

    try {
      setLoading(true);
      const response = await posService.GetPosRequestStatus(selectedPosIds);

      if (response && Array.isArray(response) && response.length > 0) {
        setRequestData(response);
      } else {
        // Fallback: If dedicated status endpoint returns empty or is not yet restarted on backend,
        // load outward and inward requests and merge them
        try {
          const [outwardRes, inwardRes] = await Promise.all([
            posService.Getposrequest(selectedPosIds),
            posService.Getnewposinwardrequests(selectedPosIds),
          ]);

          const combined: RequestStatusItem[] = [];
          const seenIds = new Set<number>();

          if (Array.isArray(inwardRes)) {
            inwardRes.forEach((inw: any) => {
              const reqId = inw.requestId || inw.id;
              if (reqId) seenIds.add(reqId);

              const batches = inw.batches || [];
              const batchCodes = batches
                .map((b: any) => b.batchCode || (b.batchId ? `B${String(b.batchId).padStart(8, "0")}` : ""))
                .filter(Boolean);

              const sentQty = batches.reduce(
                (sum: number, b: any) => sum + (Number(b.quantity) || 0),
                0
              ) || Number(inw.totalQuantity) || 0;

              combined.push({
                id: reqId,
                productId: inw.productId,
                productName: inw.productName || "",
                requestFromPOS: inw.requestFromPOS,
                requestFromPosCode: inw.requestFromPosCode || `POS-${inw.requestFromPOS}`,
                requestToPOS: inw.requestToPOS,
                requestToPosCode: inw.requestToPosCode || `POS-${inw.requestToPOS}`,
                quantity: Number(inw.quantity) || sentQty,
                sentQuantity: sentQty,
                batchNo: batchCodes.length > 0 ? Array.from(new Set(batchCodes)).join(", ") : "-",
                statusId: inw.statusId || 3,
                status: inw.status || (inw.statusId === 4 ? "Accepted" : "Sent"),
                requestedDate: inw.requestedDate || inw.respondedDate || "",
                respondedDate: inw.respondedDate || null,
              });
            });
          }

          if (Array.isArray(outwardRes)) {
            outwardRes.forEach((outw: any) => {
              const reqId = outw.id;
              if (reqId && !seenIds.has(reqId)) {
                seenIds.add(reqId);
                combined.push({
                  id: reqId,
                  productId: outw.productId,
                  productName: outw.productName || "",
                  requestFromPOS: outw.requestFromPOS,
                  requestFromPosCode: outw.requestFromPosCode || `POS-${outw.requestFromPOS}`,
                  requestToPOS: outw.requestToPOS,
                  requestToPosCode: outw.requestToPosCode || `POS-${outw.requestToPOS}`,
                  quantity: Number(outw.quantity) || 0,
                  sentQuantity: 0,
                  batchNo: "-",
                  statusId: outw.statusId || 1,
                  status: outw.status || "Pending",
                  requestedDate: outw.requestedDate || "",
                  respondedDate: outw.respondedDate || null,
                });
              }
            });
          }

          setRequestData(combined);
        } catch {
          setRequestData(Array.isArray(response) ? response : []);
        }
      }
    } catch (error) {
      console.error("Error fetching request status:", error);
      showMessage("Error", "Failed to load request status data", "error");
      setRequestData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchRequestStatus();
      setDateRange(null);
      setSelectedProduct(null);
      setSelectedRequestedBy(null);
      setSelectedRequestedTo(null);
      setSelectedBatchNo(null);
      setKeywordSearch("");
    }
  }, [visible, JSON.stringify(selectedPosIds)]);

  // Get unique product names for the product filter dropdown
  const productOptions = useMemo(() => {
    const uniqueProducts = Array.from(
      new Set(requestData.map((item) => item.productName).filter(Boolean))
    );
    return uniqueProducts.map((name) => ({
      label: name,
      value: name,
    }));
  }, [requestData]);

  // Unique options for Requested By POS dropdown
  const requestedByOptions = useMemo(() => {
    const unique = Array.from(
      new Set(requestData.map((item) => item.requestFromPosCode).filter(Boolean))
    ).sort();
    return unique.map((pos) => ({ label: pos, value: pos }));
  }, [requestData]);

  // Unique options for Requested To POS dropdown
  const requestedToOptions = useMemo(() => {
    const unique = Array.from(
      new Set(requestData.map((item) => item.requestToPosCode).filter(Boolean))
    ).sort();
    return unique.map((pos) => ({ label: pos, value: pos }));
  }, [requestData]);

  // Unique options for Batch No dropdown
  const batchOptions = useMemo(() => {
    const allBatches = new Set<string>();
    requestData.forEach((item) => {
      if (item.batchNo && item.batchNo !== "-") {
        item.batchNo.split(",").forEach((b) => {
          const trimmed = b.trim();
          if (trimmed && trimmed !== "-") {
            allBatches.add(trimmed);
          }
        });
      }
    });
    return Array.from(allBatches)
      .sort()
      .map((b) => ({ label: b, value: b }));
  }, [requestData]);

  // Filtered data based on date range, product, requested by, requested to, batch no, and keyword
  const filteredData = useMemo(() => {
    let filtered = [...requestData];

    // Filter by date (single date or date range)
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter((item) => {
        if (!item.requestedDate) return false;
        const requestDate = new Date(item.requestedDate);
        return requestDate >= startDate && requestDate <= endDate;
      });
    }

    // Filter by product
    if (selectedProduct) {
      filtered = filtered.filter(
        (item) => item.productName === selectedProduct
      );
    }

    // Filter by Requested By (which POS)
    if (selectedRequestedBy) {
      filtered = filtered.filter(
        (item) => item.requestFromPosCode === selectedRequestedBy
      );
    }

    // Filter by Requested To (which POS)
    if (selectedRequestedTo) {
      filtered = filtered.filter(
        (item) => item.requestToPosCode === selectedRequestedTo
      );
    }

    // Filter by Batch No
    if (selectedBatchNo) {
      filtered = filtered.filter(
        (item) => item.batchNo && item.batchNo.toLowerCase().includes(selectedBatchNo.toLowerCase())
      );
    }

    // Filter by keyword search (product, pos code, status, batch)
    if (keywordSearch && keywordSearch.trim() !== "") {
      const searchLower = keywordSearch.trim().toLowerCase();
      filtered = filtered.filter((item) =>
        (item.productName && item.productName.toLowerCase().includes(searchLower)) ||
        (item.requestFromPosCode && item.requestFromPosCode.toLowerCase().includes(searchLower)) ||
        (item.requestToPosCode && item.requestToPosCode.toLowerCase().includes(searchLower)) ||
        (item.status && item.status.toLowerCase().includes(searchLower)) ||
        (item.batchNo && item.batchNo.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [requestData, dateRange, selectedProduct, selectedRequestedBy, selectedRequestedTo, selectedBatchNo, keywordSearch]);

  const dateBodyTemplate = (rowData: RequestStatusItem) => {
    if (!rowData.requestedDate) return "-";

    const date = new Date(rowData.requestedDate);
    if (isNaN(date.getTime())) return rowData.requestedDate;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const statusBodyTemplate = (rowData: RequestStatusItem) => {
    const statusLower = (rowData.status || "").toLowerCase();

    let severity: "success" | "danger" | "warning" | "info" | undefined;
    let label = rowData.status;

    if (statusLower.includes("accept") || statusLower.includes("complete") || rowData.statusId === 4) {
      severity = "success";
      label = "Accepted";
    } else if (statusLower.includes("reject") || rowData.statusId === 2) {
      severity = "danger";
      label = "Rejected";
    } else if (statusLower.includes("pend") || rowData.statusId === 1) {
      severity = "warning";
      label = "Pending";
    } else if (statusLower.includes("sent") || rowData.statusId === 3) {
      severity = "info";
      label = "Sent";
    } else {
      severity = "info";
    }

    return <Tag value={label} severity={severity} />;
  };

  const sentQtyBodyTemplate = (rowData: RequestStatusItem) => {
    return (
      <span className={rowData.sentQuantity > 0 ? "font-semibold text-green-700" : ""}>
        {rowData.sentQuantity ?? 0}
      </span>
    );
  };

  const batchNoBodyTemplate = (rowData: RequestStatusItem) => {
    return rowData.batchNo || "-";
  };

  const getStatusLabel = (rowData: RequestStatusItem) => {
    const statusLower = (rowData.status || "").toLowerCase();
    if (statusLower.includes("accept") || statusLower.includes("complete") || rowData.statusId === 4) {
      return "Accepted";
    } else if (statusLower.includes("reject") || rowData.statusId === 2) {
      return "Rejected";
    } else if (statusLower.includes("pend") || rowData.statusId === 1) {
      return "Pending";
    } else if (statusLower.includes("sent") || rowData.statusId === 3) {
      return "Sent";
    }
    return rowData.status || "Pending";
  };

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      showMessage("Export", "No data available to export", "warn");
      return;
    }

    const exportRows = filteredData.map((item) => ({
      "Requested Date": dateBodyTemplate(item),
      "Product Name": item.productName || "-",
      "Requested By (POS)": item.requestFromPosCode || "-",
      "Requested To (POS)": item.requestToPosCode || "-",
      "Requested Qty": item.quantity ?? 0,
      "Sent Qty": item.sentQuantity ?? 0,
      "Batch No": item.batchNo || "-",
      "Status": getStatusLabel(item),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);

    ws["!cols"] = [
      { wch: 16 }, // Requested Date
      { wch: 32 }, // Product Name
      { wch: 20 }, // Requested By (POS)
      { wch: 20 }, // Requested To (POS)
      { wch: 15 }, // Requested Qty
      { wch: 12 }, // Sent Qty
      { wch: 22 }, // Batch No
      { wch: 15 }, // Status
    ];

    XLSX.utils.book_append_sheet(wb, ws, "RequestStatus");
    XLSX.writeFile(wb, `POS_Request_Status_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const resetFilters = () => {
    setDateRange(null);
    setSelectedProduct(null);
    setSelectedRequestedBy(null);
    setSelectedRequestedTo(null);
    setSelectedBatchNo(null);
    setKeywordSearch("");
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Request Status"
        modal
        style={{ width: "85vw" }}
        breakpoints={{ "960px": "92vw", "641px": "98vw" }}
      >
        {/* Filters Section */}
        <div className="flex flex-wrap gap-3 mb-3 align-items-end">
          {/* Date Range Filter */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Filter by Date</label>
            <Calendar
              value={dateRange as any}
              onChange={(e) => setDateRange(e.value as Date[] | null)}
              selectionMode="range"
              readOnlyInput
              placeholder="Select date range"
              dateFormat="dd-mm-yy"
              showIcon
              showButtonBar
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                minWidth: "240px",
              }}
            />
          </div>

          {/* Product Filter */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Product</label>
            <Dropdown
              value={selectedProduct}
              options={productOptions}
              onChange={(e) => setSelectedProduct(e.value)}
              placeholder="All Products"
              filter
              showClear
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                minWidth: "180px",
              }}
            />
          </div>

          {/* Requested By (which POS) Filter */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Requested By (POS)</label>
            <Dropdown
              value={selectedRequestedBy}
              options={requestedByOptions}
              onChange={(e) => setSelectedRequestedBy(e.value)}
              placeholder="All POS"
              filter
              showClear
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                minWidth: "150px",
              }}
            />
          </div>

          {/* Requested To (which POS) Filter */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Requested To (POS)</label>
            <Dropdown
              value={selectedRequestedTo}
              options={requestedToOptions}
              onChange={(e) => setSelectedRequestedTo(e.value)}
              placeholder="All POS"
              filter
              showClear
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                minWidth: "150px",
              }}
            />
          </div>

          {/* Batch No Filter */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Batch No</label>
            <Dropdown
              value={selectedBatchNo}
              options={batchOptions}
              onChange={(e) => setSelectedBatchNo(e.value)}
              placeholder="All Batches"
              filter
              showClear
              style={{
                border: "1px solid #ced4da",
                borderRadius: "4px",
                minWidth: "150px",
              }}
            />
          </div>

          {/* Keyword Search */}
          <div className="flex flex-column gap-1">
            <label className="font-semibold text-sm">Search</label>
            <div className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                value={keywordSearch}
                onChange={(e) => setKeywordSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  minWidth: "140px",
                }}
              />
            </div>
          </div>

          {/* Action Buttons: Reset & Export Excel */}
          <div className="flex gap-2">
            <Button
              label="Reset"
              icon="pi pi-refresh"
              severity="secondary"
              text
              raised
              onClick={resetFilters}
            />
            <Button
              label="Export Excel"
              icon="pi pi-file-excel"
              severity="success"
              text
              raised
              onClick={exportToExcel}
              disabled={!filteredData || filteredData.length === 0}
              tooltip="Export displayed data to Excel"
              tooltipOptions={{ position: "top" }}
            />
          </div>
        </div>

        {/* Data Table */}
        {filteredData && filteredData.length > 0 ? (
          <DataTable
            value={filteredData}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            loading={loading}
            emptyMessage="No request status records found."
            sortField="requestedDate"
            sortOrder={-1}
            scrollable
            scrollHeight="400px"
          >
            <Column
              field="requestedDate"
              header="Requested Date"
              body={dateBodyTemplate}
              sortable
              style={{ minWidth: "130px" }}
            />
            <Column
              field="productName"
              header="Product Name"
              sortable
              style={{ minWidth: "180px" }}
            />
            <Column
              field="requestFromPosCode"
              header="Requested By (POS)"
              sortable
              style={{ minWidth: "140px" }}
            />
            <Column
              field="requestToPosCode"
              header="Requested To (POS)"
              sortable
              style={{ minWidth: "140px" }}
            />
            <Column
              field="quantity"
              header="Requested Qty"
              sortable
              style={{ minWidth: "120px" }}
            />
            <Column
              field="sentQuantity"
              header="Sent Qty"
              body={sentQtyBodyTemplate}
              sortable
              style={{ minWidth: "110px" }}
            />
            <Column
              field="batchNo"
              header="Batch No"
              body={batchNoBodyTemplate}
              sortable
              style={{ minWidth: "140px" }}
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
              style={{ minWidth: "120px" }}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4">
            <p className="text-gray-500">
              {loading
                ? "Loading request status data..."
                : "No request status records found."}
            </p>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default RequestStatusDialog;
